/**
 * Persist last-viewed products per user/guest for "Recently viewed" (max 20).
 * Stored in localStorage; payload matches list-style shape for ProductCard.
 */

/** Oldest dropped when exceeded (FIFO cap per user bucket). */
const MAX_ITEMS = 20;
const STORAGE_VERSION = "v2";

export const getRecentProductsStorageKey = (userKey) =>
  `yug_recent_products_${STORAGE_VERSION}_${userKey ?? "guest"}`;

/** @returns {string} */
export const getRecentProductsUserKey = (userData) =>
  userData?._id != null ? String(userData._id) : "guest";

/**
 * @param {object} product - full product from details API
 * @param {Array<{ image?: string }>} imageList - same order as gallery (e.g. sortedImages)
 */
export function buildRecentProductSnapshot(product, imageList) {
  if (!product?._id) return null;
  const rawSizes = Array.isArray(product.sizes)
    ? product.sizes
    : Array.isArray(product.size)
      ? product.size
      : [];
  const sizes = rawSizes.map((sz) => {
    if (!sz || typeof sz !== "object") return sz;
    return {
      ...sz,
      retail_price: sz.retail_price ?? sz.price ?? sz.selling_price,
      wholesale_price: sz.wholesale_price ?? sz.wholesalePrice,
    };
  });
  const firstPath =
    imageList?.[0]?.image ||
    product.images?.[0]?.image ||
    product.image?.[0]?.image;
  const imageWrap = firstPath ? [{ image: firstPath }] : [];
  return {
    _id: product._id,
    id: product._id,
    name: product.name,
    size: sizes,
    mrp: product.mrp ?? product.original_price,
    original_price: product.original_price,
    image: imageWrap.length ? imageWrap : product.image || [],
    colors: product.colors,
    color: product.color,
    fabric: product.fabric,
    is_sold_out: product.is_sold_out,
    sold_out: product.sold_out,
    in_stock: product.in_stock,
    stock: product.stock,
  };
}

function readRawList(userKey) {
  try {
    const raw = localStorage.getItem(getRecentProductsStorageKey(userKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    if (list.length <= MAX_ITEMS) return list;
    const trimmed = list.slice(0, MAX_ITEMS);
    writeRawList(userKey, trimmed);
    return trimmed;
  } catch {
    return [];
  }
}

function writeRawList(userKey, list) {
  try {
    localStorage.setItem(
      getRecentProductsStorageKey(userKey),
      JSON.stringify(list)
    );
  } catch {
    /* quota / privacy mode */
  }
}

/**
 * @param {string} userKey from getRecentProductsUserKey
 * @param {object|null} snapshot from buildRecentProductSnapshot
 */
export function recordRecentlyViewedProduct(userKey, snapshot) {
  if (!snapshot || !snapshot._id) return;
  const id = String(snapshot._id);
  const prev = readRawList(userKey).filter((p) => p && String(p._id || p.id) !== id);
  prev.unshift(snapshot);
  // Newest first; trim so 21st+ view drops oldest from history
  writeRawList(userKey, prev.slice(0, MAX_ITEMS));
}

/**
 * Returns stored snapshots excluding the current PDP id (oldest trimmed already).
 */
export function getRecentlyViewedProducts(userKey, excludeProductId = null) {
  const exclude =
    excludeProductId != null && excludeProductId !== ""
      ? String(excludeProductId)
      : null;
  const list = readRawList(userKey).filter(Boolean).slice(0, MAX_ITEMS);
  if (!exclude) return list;
  return list.filter((p) => String(p._id || p.id) !== exclude);
}
